/* eslint-disable react/display-name */ // react-select + chakra implementation taken from:
// https://gist.github.com/csandman/c687a9fb4275112f281ab9a5701457e4
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import Select, { components as selectComponents } from "react-select";
import { chakra, Flex, Tag, TagCloseButton, TagLabel, Divider, CloseButton, Center, Portal, StylesProvider, useMultiStyleConfig, useStyles, useTheme, useColorModeValue, Icon } from "@chakra-ui/react";
import { FiChevronDown } from "react-icons/fi";
var chakraStyles = {
    input: function(provided) {
        return _object_spread_props(_object_spread({}, provided), {
            color: "inherit",
            lineHeight: 1
        });
    },
    menu: function(provided) {
        return _object_spread_props(_object_spread({}, provided), {
            boxShadow: "none"
        });
    },
    valueContainer: function(provided) {
        return _object_spread_props(_object_spread({}, provided), {
            padding: "0.125rem 1rem"
        });
    }
};
var chakraComponents = {
    // Control components
    Control: function(param) {
        var children = param.children, innerRef = param.innerRef, innerProps = param.innerProps, isDisabled = param.isDisabled, isFocused = param.isFocused;
        var inputStyles = useMultiStyleConfig("Input", {});
        return /*#__PURE__*/ _jsx(StylesProvider, {
            value: inputStyles,
            children: /*#__PURE__*/ _jsx(Flex, _object_spread_props(_object_spread({
                ref: innerRef,
                sx: _object_spread_props(_object_spread({}, inputStyles.field), {
                    p: 0,
                    overflow: "hidden",
                    h: "auto",
                    minH: 10
                })
            }, innerProps, isFocused && {
                "data-focus": true
            }, isDisabled && {
                disabled: true
            }), {
                children: children
            }))
        });
    },
    MultiValueContainer: function(param) {
        var children = param.children, innerRef = param.innerRef, innerProps = param.innerProps, isFixed = param.data.isFixed;
        return /*#__PURE__*/ _jsx(Tag, _object_spread_props(_object_spread({
            ref: innerRef
        }, innerProps), {
            m: "0.125rem",
            variant: isFixed ? "solid" : "subtle",
            children: children
        }));
    },
    MultiValueLabel: function(param) {
        var children = param.children, innerRef = param.innerRef, innerProps = param.innerProps;
        return /*#__PURE__*/ _jsx(TagLabel, _object_spread_props(_object_spread({
            ref: innerRef
        }, innerProps), {
            children: children
        }));
    },
    MultiValueRemove: function(param) {
        var children = param.children, innerRef = param.innerRef, innerProps = param.innerProps, isFixed = param.data.isFixed;
        if (isFixed) {
            return null;
        }
        return /*#__PURE__*/ _jsx(TagCloseButton, _object_spread_props(_object_spread({
            ref: innerRef
        }, innerProps), {
            children: children
        }));
    },
    IndicatorSeparator: function(param) {
        var innerProps = param.innerProps;
        return /*#__PURE__*/ _jsx(Divider, _object_spread_props(_object_spread({}, innerProps), {
            orientation: "vertical",
            opacity: "1"
        }));
    },
    ClearIndicator: function(param) {
        var innerProps = param.innerProps;
        return /*#__PURE__*/ _jsx(CloseButton, _object_spread_props(_object_spread({}, innerProps), {
            size: "sm",
            mx: 2
        }));
    },
    DropdownIndicator: function(param) {
        var innerProps = param.innerProps;
        var addon = useStyles().addon;
        return /*#__PURE__*/ _jsx(Center, _object_spread_props(_object_spread({}, innerProps), {
            sx: _object_spread_props(_object_spread({}, addon), {
                h: "100%",
                borderRadius: 0,
                borderWidth: 0,
                cursor: "pointer"
            }),
            children: /*#__PURE__*/ _jsx(Icon, {
                as: FiChevronDown,
                h: 5,
                w: 5
            })
        }));
    },
    // Menu components
    MenuPortal: function(param) {
        var children = param.children;
        return /*#__PURE__*/ _jsx(Portal, {
            children: children
        });
    },
    Menu: function(_param) {
        var children = _param.children, props = _object_without_properties(_param, [
            "children"
        ]);
        var menuStyles = useMultiStyleConfig("Menu", {});
        return /*#__PURE__*/ _jsx(selectComponents.Menu, _object_spread_props(_object_spread({}, props), {
            children: /*#__PURE__*/ _jsx(StylesProvider, {
                value: menuStyles,
                children: children
            })
        }));
    },
    MenuList: function(param) {
        var innerRef = param.innerRef, children = param.children, maxHeight = param.maxHeight;
        var list = useStyles().list;
        return /*#__PURE__*/ _jsx(chakra.div, {
            sx: _object_spread_props(_object_spread({}, list), {
                maxH: "".concat(maxHeight, "px"),
                overflowY: "auto"
            }),
            ref: innerRef,
            children: children
        });
    },
    GroupHeading: function(param) {
        var innerProps = param.innerProps, children = param.children;
        var groupTitle = useStyles().groupTitle;
        return /*#__PURE__*/ _jsx(chakra.div, _object_spread_props(_object_spread({
            sx: groupTitle
        }, innerProps), {
            children: children
        }));
    },
    Option: function(param) {
        var innerRef = param.innerRef, innerProps = param.innerProps, children = param.children, isFocused = param.isFocused, isDisabled = param.isDisabled;
        var item = useStyles().item;
        return /*#__PURE__*/ _jsx(chakra.div, _object_spread_props(_object_spread({
            role: "button",
            sx: _object_spread(_object_spread_props(_object_spread({}, item), {
                w: "100%",
                textAlign: "left",
                bg: isFocused ? item._focus.bg : "transparent"
            }), isDisabled && item._disabled),
            ref: innerRef
        }, innerProps, isDisabled && {
            disabled: true
        }), {
            children: children
        }));
    }
};
export function MultiSelect(_param) {
    var _name = _param.name, name = _name === void 0 ? "" : _name, _styles = _param.styles, styles = _styles === void 0 ? {} : _styles, _components = _param.components, components = _components === void 0 ? {} : _components, theme = _param.theme, _placeholder = _param.placeholder, placeholder = _placeholder === void 0 ? "" : _placeholder, selectProps = _object_without_properties(_param, [
        "name",
        "styles",
        "components",
        "theme",
        "placeholder"
    ]);
    var chakraTheme = useTheme();
    var placeholderColor = useColorModeValue(chakraTheme.colors.gray[400], chakraTheme.colors.whiteAlpha[400]);
    return /*#__PURE__*/ _jsx(Select, _object_spread({
        name: name,
        placeholder: placeholder === "" ? "S\xe9lectionner" : placeholder,
        components: _object_spread({}, chakraComponents, components),
        styles: _object_spread({}, chakraStyles, styles),
        theme: function(baseTheme) {
            return _object_spread_props(_object_spread({}, baseTheme), {
                borderRadius: chakraTheme.radii.md,
                colors: _object_spread(_object_spread_props(_object_spread({}, baseTheme.colors), {
                    neutral50: placeholderColor,
                    neutral40: placeholderColor
                }), theme === null || theme === void 0 ? void 0 : theme.colors),
                spacing: _object_spread({}, baseTheme.spacing, theme === null || theme === void 0 ? void 0 : theme.spacing)
            });
        }
    }, selectProps));
}
export default MultiSelect;
